import AIcon from '@/components/A/AIcon/AIcon'
import styles from './ProjectCreatePage.module.less'
import ASteps from '@/components/A/ASteps/ASteps'
import { useEffect, useState } from 'react'
import StepOne from './components/StepOne/StepOne'
import StepTwo from './components/StepTwo/StepTwo'
import { DASHBOARD_CONTENT_CONTAINER_ID, PAGE_LINKS } from '@/router/pageConst'
import { api } from '@/api'
import { message } from 'antd'
import { useRequest } from 'alova/client'
import { useTranslation } from 'react-i18next'
import MiddleText from '@/components/MiddleText/MiddleText'
import { corpService } from '@/services/corpService'
import { observer } from 'mobx-react'
import useNewNavigate from '@/helpers/commonHooks/useNewNavigate'
import useProductType from '@/helpers/commonHooks/useProductType'
import { INTERNAL_HOST } from '@/commonConst'
import { isQuickProductCategory, isSurveyProductCategory } from '@/helpers/productCategory'
import { resolveOutputLanguageByI18nLanguage } from '@/helpers/outputLanguage'

const INTERNAL_DEFAULT_OUTPUT_LANGUAGE: ProjectModels.UseLanguage = 'CN'
const OVERSEAS_DEFAULT_OUTPUT_LANGUAGE: ProjectModels.UseLanguage = 'EN'

function ProjectCreatePage() {
  const { t, i18n } = useTranslation()
  const { productType } = useProductType()
  const isCreativeDiagnosisMode = !isQuickProductCategory(productType) && !isSurveyProductCategory(productType)
  const defaultOutputLanguage = INTERNAL_HOST.includes(location.hostname)
    ? INTERNAL_DEFAULT_OUTPUT_LANGUAGE
    : OVERSEAS_DEFAULT_OUTPUT_LANGUAGE
  const stepItems = [
    { title: t('dashboard.projectCreatePage.step.step1') },
    { title: t('dashboard.projectCreatePage.step.step2') },
  ]
  const navigate = useNewNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [projectCreateParam, setProjectCreateParam] = useState<
    ProjectModels.CreateProjectParam | undefined
  >()
  const {
    data: { corpAnalyseCount: analysisCnt },
    send: refreshAnalysisCnt,
  } = useRequest(() => api.corp.analysisCnt({ projectCategory: productType }), { immediate: false, initialData: { corpAnalysisCount: 0 } })

  const onSubmit = async () => {
    try {
      const createParam = isCreativeDiagnosisMode
        ? {
          ...projectCreateParam!,
          useLanguage: resolveOutputLanguageByI18nLanguage(i18n.language, defaultOutputLanguage),
        }
        : projectCreateParam!
      const createRequest = api.project.projectCreate(createParam).send()
      const checkWaitingRequest = api.project.checkWaiting().send()
      const [{ projectId }, { canAnalyse }] = await Promise.all([createRequest, checkWaitingRequest])
      message.success(t('dashboard.projectCreatePage.message.success'))
      if (!canAnalyse) {
        message.warning(t('dashboard.projectPage.message.waitingQuestion'))
      }
      navigate(PAGE_LINKS.projectDetail, { state: { id: projectId } })
      corpService.refreshFreeTrialStatus(productType, { force: true })
    } catch (e) {
      const error = e as ApiError
      message.error(error.statusMessage)
    }
  }

  useEffect(() => {
    const dashboardContent = document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
    dashboardContent?.scrollTo({ top: 0 })
    corpService.refreshFreeTrialStatus(productType)
  }, [productType])


  return (
    <div className={styles.projectCreatePage} data-testid='project-create-page'>
      <div className={styles.returnRow}>
        <div
          className={styles.returnBtn}
          data-testid='project-create-return-btn'
          onClick={() => {
            navigate(PAGE_LINKS.project)
          }}
        >
          <span className={styles.iconBox}>
            <AIcon type='icon-line-global_left' width={14} height={14}></AIcon>
          </span>
          <span className={styles.btnText}>
            <MiddleText text={t('dashboard.projectCreatePage.createProject')} />
          </span>
        </div>
      </div>
      <div className={styles.createContent}>
        <div className={styles.stepBox}>
          <ASteps testId='project-create-steps' items={stepItems} current={currentStep}></ASteps>
        </div>
        <StepOne
          className={currentStep === 0 ? styles.visible : styles.hidden}
          onNext={(param) => {
            setProjectCreateParam(param)
            setCurrentStep((step) => step + 1)
            refreshAnalysisCnt()
          }}
        />
        {currentStep === 1 && (
          <StepTwo
            analysisCnt={analysisCnt}
            projectCategory={productType}
            param={projectCreateParam}
            onPrev={() => {
              setCurrentStep((step) => step - 1)
              refreshAnalysisCnt()
            }}
            onSubmit={onSubmit}
          />
        )}
      </div>
    </div>
  )
}

export default observer(ProjectCreatePage)
